import { removeNull, resetAttribute } from '@/utils/handleData.js'
import {
  getDefaultBidUid,
  setDefaultBidUid,
  isDefaultBidUid
} from '@/utils/defaultBid.js'
import { buildDiscloseQcodeInfo } from '@/utils/scanQrcode.js'

const BID_QUERY_FIELD_KEYS = [
  'upVproUid',
  'vProjectUid',
  'vprojectUid',
  'orgUid'
]

function resolveBidFieldKey(queryValues, preferred) {
  if (preferred) return preferred
  if (!queryValues) return 'upVproUid'
  for (let i = 0; i < BID_QUERY_FIELD_KEYS.length; i++) {
    const key = BID_QUERY_FIELD_KEYS[i]
    if (Object.prototype.hasOwnProperty.call(queryValues, key)) return key
  }
  // projectUid 可能是「所属工程」，仅在显式传入时使用
  if (Object.prototype.hasOwnProperty.call(queryValues, 'projectUid')) {
    return ''
  }
  return 'upVproUid'
}


function buildBidQcodeInfo(info, uid) {
  const base = {
    qcodeUrl: (info && (info.qcode || info.qcodeUrl)) || uid,
    qcodeName: (info && (info.projectCode || info.orgName)) || '',
    project: '小程序个人备案扫码',
    leftText: '用诚信企业',
    rightText: '干品质工程'
  }
  return Object.assign(base, {
    discloseQcodeInfo: buildDiscloseQcodeInfo(info, uid)
  })
}

function applyWorkbenchParent(rootState, rootGetters, payload) {
  if (
    !payload ||
    !rootState.currentStep ||
    rootState.currentStep.theme !== 'projectSection' ||
    !rootState.user.orgValue
  ) {
    return payload
  }
  const orgType = rootGetters['user/orgType']
  if (orgType === 'com') {
    payload.parentCompanyUid = rootState.user.orgValue
  } else if (orgType === 'bigPro') {
    payload.projectUid = rootState.user.orgValue
  }
  return payload
}

function pickText(obj, keys) {
  if (!obj) return ''
  for (let i = 0; i < keys.length; i++) {
    const value = obj[keys[i]]
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim()
    }
  }
  return ''
}

function pickBuildStatus(item) {
  return pickText(item, ['buildStatus', 'buildstatus', 'build_status'])
}

function matchDictLabel(list, value) {
  if (value === undefined || value === null || String(value).trim() === '') {
    return ''
  }
  const raw = String(value).trim()
  const options = Array.isArray(list) ? list : []
  const hit = options.find((item) => {
    if (!item) return false
    return (
      String(item.value) === raw ||
      String(item.uid) === raw ||
      String(item.activityUid) === raw ||
      String(item.label) === raw ||
      String(item.type) === raw ||
      String(item.activityType) === raw
    )
  })
  if (!hit) return raw
  return hit.label || hit.type || hit.activityType || raw
}

function bidOptionLabel(name, status, dictList) {
  const label = name || ''
  let state = status ? String(status).trim() : ''
  if (!state) return label
  const resolved = matchDictLabel(dictList, state)
  if (resolved) state = String(resolved).trim()
  // 未解析到字典名的 uid，不拼进下拉文案（避免「名称(uuid)」乱码感）
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      state
    )
  ) {
    return label
  }
  if (!state || state === '在建') return label
  return label + '（' + state + '）'
}

export default {
  namespaced: true,
  state: {
    bidSectionList: {}, // 项目标段列表
    bidSectionDetail: null, // 缓存当前标段详情，避免编辑时重复请求
    bidSectionBaseInfo: {}, // 项目标段基础信息
    bidSectionOverview: {}, // 项目标段工程概况
    bidSectionContract: {}, // 项目标段承包信息
    relevantBid: [], // 当前选择机构同一工程下所有标段
    subLevelOrg: [], // 某机构下所有相关组织机构，包含它自身,范围至劳务公司级,数据不分级
    subBidSection: [], // 某机构下所有项目标段，如果它是项目标段则包含它自身
    manageCompanyNames: [], // 当前默认或选择项目标段的所属企业及建设单位
    laborCompanyNames: [], // 当前默认或选择项目标段下属的劳务公司
    unExitLaborCompanys: [] // 当前默认或选择项目标段下属的未退场劳务公司
  },
  getters: {
    // 当前选择机构同一工程下无关联标段
    unconnectedBid: (state, getters, rootState) => {
      var unconnectedBid = JSON.parse(JSON.stringify(state.relevantBid))
      rootState.organization.organRelations.forEach((org) => {
        unconnectedBid.forEach((item, index) => {
          if (org.startUid === item.orgUid) {
            unconnectedBid.splice(index, 1)
          }
        })
      })
      return unconnectedBid
    },
    // 某机构下属未进场的项目标段
    notEnteredBid: (state, getters, rootState) => {
      var notEnteredBid = JSON.parse(JSON.stringify(state.subBidSection))
      if (rootState.contractor.manageCompanyList.result.length) {
        notEnteredBid = notEnteredBid.filter((item) => {
          return rootState.contractor.manageCompanyList.result.some((org) => {
            return item.orgUid !== org.orgUid
          })
        })
      }
      return notEnteredBid
    }
  },
  actions: {
    /**
     * 企业/工程级列表：初始化「所属项目标段」筛选。
     * @param queryKey queryValues 的 key
     * @param fieldKey 标段字段名，默认自动识别
     * @param force 进页强制用已标记默认标段覆盖（仍保留用户在本页手动改选）
     */
    async ensureDefaultBidQuery(
      { rootState, rootGetters, commit, dispatch },
      payload = {}
    ) {
      const queryKey = payload.queryKey
      if (!queryKey) return {}
      const orgType = rootGetters['user/orgType']
      let queryValues = Object.assign({}, rootState.queryValues[queryKey] || {})
      // 班前教育：企业/工程级不自动填充默认标段，需查下级全部标段
      if (queryKey === 'preClassEducation' || payload.skipDefault) {
        return queryValues
      }
      if (orgType === 'simPro' || orgType === 'laborCom') {
        return queryValues
      }
      const fieldKey = resolveBidFieldKey(queryValues, payload.fieldKey)
      if (!fieldKey) return queryValues
      const orgUid = rootState.user && rootState.user.orgValue
      const current = queryValues[fieldKey]
      const isUnset =
        current === undefined ||
        current === null ||
        current === '' ||
        (orgUid && String(current) === String(orgUid))
      const markedUid = getDefaultBidUid(orgUid)
      // 本机没有时，尝试从服务端拉一次（换设备首次进业务页）
      if (!markedUid && (orgType === 'com' || orgType === 'bigPro')) {
        await dispatch('syncDefaultBidFromServer')
      }
      const syncedUid = getDefaultBidUid(orgUid)
      const force = !!payload.force
      // 进页强制：有标记则覆盖；无标记且已有有效标段则保留
      if (!force && !isUnset) return queryValues
      if (force && !syncedUid && !isUnset) return queryValues

      let bids = rootState.bidSection.subBidSection || []
      if (!bids.length) {
        if (orgUid) {
          await dispatch('getSubBidSection', orgUid)
          bids = rootState.bidSection.subBidSection || []
        }
      }
      const marked =
        syncedUid &&
        bids.find((item) => item && String(item.value) === String(syncedUid))
      const first = bids.find((item) => item && item.value)
      const pick = marked || (isUnset || force ? first : null)
      if (pick && pick.value) {
        queryValues = Object.assign({}, queryValues, {
          [fieldKey]: String(pick.value)
        })
        commit(
          'queryValuesSet',
          {
            key: queryKey,
            value: queryValues
          },
          { root: true }
        )
        try {
          await dispatch('getLaborCompanyNames', pick.value)
        } catch (e) {
          /* ignore */
        }
      }
      return queryValues
    },
    /** 进页：仅当存在「所属项目标段」类筛选项时，强制套用默认标段 */
    async initPageDefaultBidQuery(
      { dispatch, rootState },
      payload = {}
    ) {
      const queryKey = payload.queryKey
      if (!queryKey || queryKey === 'bidSection') {
        return (rootState.queryValues && rootState.queryValues[queryKey]) || {}
      }
      let fieldKey = payload.fieldKey
      if (!fieldKey) {
        const selectData = payload.selectData
        if (Array.isArray(selectData)) {
          const hit = selectData.find((item) => {
            if (!item || !item.key || !item.label) return false
            const label = String(item.label)
            // 明确排除「所属工程」，只认标段筛选
            if (label.indexOf('所属工程') !== -1) return false
            return (
              label.indexOf('项目标段') !== -1 ||
              label.indexOf('项目/标段') !== -1 ||
              label === '项目标段' ||
              label === '标段'
            )
          })
          if (hit) fieldKey = hit.key
        }
      }
      if (!fieldKey) {
        return (rootState.queryValues && rootState.queryValues[queryKey]) || {}
      }
      const queryValues = rootState.queryValues[queryKey] || {}
      if (!Object.prototype.hasOwnProperty.call(queryValues, fieldKey)) {
        return queryValues
      }
      return dispatch('ensureDefaultBidQuery', {
        queryKey: queryKey,
        fieldKey: fieldKey,
        force: true
      })
    },
    async markDefaultBid({ rootState, rootGetters, dispatch }) {
      const orgType = rootGetters['user/orgType']
      if (orgType !== 'com' && orgType !== 'bigPro') {
        return { code: 400, msg: '仅企业或工程级可标记默认标段' }
      }
      const orgUid = rootState.user && rootState.user.orgValue
      const selection =
        (rootState.selection && rootState.selection.bidSection) || []
      if (!selection.length) {
        return { code: 400, msg: '请先单选一个项目标段' }
      }
      if (selection.length > 1) {
        return { code: 400, msg: '只能单选一个项目标段作为默认' }
      }
      const bidUid = selection[0] && selection[0].orgUid
      if (!bidUid) {
        return { code: 400, msg: '未获取到标段信息' }
      }
      // 先落库（跨设备），再写本机缓存
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/temp/defaultbid',
          data: { orgUid, bidUid },
          msg: false
        },
        { root: true }
      )
      if (!res || res.code !== 200) {
        return res || { code: 500, msg: '保存默认标段失败' }
      }
      setDefaultBidUid(orgUid, bidUid)
      await dispatch('getBidSectionList')
      return { code: 200, msg: '已标记为默认项目标段' }
    },
    /** 从服务端同步默认标段到本机缓存（登录后/列表刷新） */
    async syncDefaultBidFromServer({ rootState, rootGetters, dispatch }) {
      const orgType = rootGetters['user/orgType']
      if (orgType !== 'com' && orgType !== 'bigPro') return ''
      const orgUid = rootState.user && rootState.user.orgValue
      if (!orgUid) return ''
      try {
        const res = await dispatch(
          'baseRequest',
          {
            method: 'GET',
            url: '/organize/temp/defaultbid',
            data: { orgUid },
            noErrorMsg: true
          },
          { root: true }
        )
        const bidUid =
          res && res.data && res.data.bidUid ? String(res.data.bidUid) : ''
        setDefaultBidUid(orgUid, bidUid)
        return bidUid
      } catch (e) {
        return getDefaultBidUid(orgUid)
      }
    },
    // 获取某项目标段同一工程项目下所有相关项目标段
    async getRelevantBid({ commit, dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/query/orglevel/project',
          data: { upVproUid: uid }
        },
        { root: true }
      )
      if (res.data) {
        const relevantBid = resetAttribute(res.data, 'orgCode', 'orgUid')
        commit(
          'baseSet',
          { module: 'bidSection', key: 'relevantBid', value: relevantBid },
          { root: true }
        )
      }
    },
    // 获取某组织机构下所有相关组织（到劳务公司级）
    async getSubLevelOrg({ commit, dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/query/laborclock/clockorg',
          data: { orgUid: uid }
        },
        { root: true }
      )
      if (res.data) {
        const subLevelOrg = resetAttribute(res.data, 'orgName', 'orgUid')
        commit(
          'baseSet',
          { module: 'bidSection', key: 'subLevelOrg', value: subLevelOrg },
          { root: true }
        )
      }
    },
    // 某机构下所有机构
    async getSubBidSection({ commit, dispatch, rootGetters, rootState }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/project/downorg',
          data: { orgUid: uid }
        },
        { root: true }
      )
      const subLevelOrg = resetAttribute(
        res.data ? res.data : [],
        'orgCode',
        'orgUid'
      )
      const statusMap = {}
      const parentMap = {}
      const contractMap = {}
      const createTimeMap = {}
      const listData = { page: 1, limit: 1000 }
      const orgType = rootGetters['user/orgType']
      if (orgType === 'bigPro') {
        listData.projectUid = uid
      } else if (orgType === 'com') {
        listData.parentCompanyUid = uid
      }
      const listRes = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/temp/projectinfolist',
          data: listData,
          noErrorMsg: true
        },
        { root: true }
      )
      const rows =
        listRes && listRes.data && Array.isArray(listRes.data.result)
          ? listRes.data.result
          : []
      rows.forEach((row) => {
        const rowUid = row && (row.orgUid || row.uid)
        if (rowUid) {
          const key = String(rowUid)
          statusMap[key] = pickBuildStatus(row)
          parentMap[key] = pickText(row, ['parentCompanyUid'])
          contractMap[key] = pickText(row, ['contractCompanyUid'])
          createTimeMap[key] = pickText(row, [
            'createTime',
            'create_time',
            'orgBeginTime',
            'org_begin_time',
            'planStartDate',
            'plan_start_date'
          ])
        }
      })
      let statusDict =
        (rootState.dictionarys && rootState.dictionarys.buildStatus) || []
      if (!statusDict.length) {
        try {
          await dispatch('dictionary/getBuildStatus', null, { root: true })
          statusDict =
            (rootState.dictionarys && rootState.dictionarys.buildStatus) || []
        } catch (e) {
          statusDict = []
        }
      }
      const seen = {}
      const subBidSection = subLevelOrg.filter((item) => {
        const vType = (item && (item.vType || item.vtype)) || ''
        const value = item && (item.value || item.orgUid)
        if (vType !== 'simPro' || !value || seen[String(value)]) {
          return false
        }
        seen[String(value)] = true
        item.buildStatus =
          pickBuildStatus(item) || statusMap[String(value)] || ''
        item.parentCompanyUid =
          pickText(item, ['parentCompanyUid']) || parentMap[String(value)] || ''
        item.contractCompanyUid =
          pickText(item, ['contractCompanyUid']) ||
          contractMap[String(value)] ||
          ''
        item.createTime =
          pickText(item, ['createTime', 'create_time']) ||
          createTimeMap[String(value)] ||
          pickText(item, [
            'orgBeginTime',
            'org_begin_time',
            'planStartDate',
            'plan_start_date'
          ])
        item.label = bidOptionLabel(
          item.orgCode || item.orgName || item.label || String(value),
          item.buildStatus,
          statusDict
        )
        item.value = String(value)
        return true
      })
      const currentType = rootGetters['user/orgType']
      if (
        currentType === 'simPro' &&
        uid &&
        !subBidSection.some((item) => item && String(item.value) === String(uid))
      ) {
        const status = statusMap[String(uid)] || ''
        const name = rootGetters['user/orgName'] || uid
        subBidSection.unshift({
          label: bidOptionLabel(name, status, statusDict),
          value: uid,
          orgUid: uid,
          orgCode: name,
          buildStatus: status,
          createTime: createTimeMap[String(uid)] || '',
          vType: 'simPro'
        })
      }
      commit(
        'baseSet',
        { module: 'bidSection', key: 'subLevelOrg', value: subLevelOrg },
        { root: true }
      )
      commit(
        'baseSet',
        { module: 'bidSection', key: 'subBidSection', value: subBidSection },
        { root: true }
      )
      return subLevelOrg
    },
    // 当前默认或选择项目标段下属的所有劳务公司
    async getLaborCompanyNames({ commit, dispatch }, uid) {
      if (!uid) {
        commit(
          'baseSet',
          {
            module: 'bidSection',
            key: 'laborCompanyNames',
            value: []
          },
          { root: true }
        )
        return []
      }
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/query/team/levelinfo',
          data: { organUid: uid }
        },
        { root: true }
      )
      const rows = Array.isArray(res && res.data) ? res.data : []
      const laborCompanyNames = resetAttribute(
        rows,
        'vProjectName',
        'vProjectUid'
      ).filter((item) => {
        const parentUid = item.upVproUid || item.upvprouid || item.parentUid
        return parentUid === uid
      })
      commit(
        'baseSet',
        {
          module: 'bidSection',
          key: 'laborCompanyNames',
          value: laborCompanyNames
        },
        { root: true }
      )
      return laborCompanyNames
    },
    // 当前默认或选择项目标段下属的未退场场劳务公司
    async getUnExitLaborCompanys({ rootState, commit, dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/query/team/levelinfoexit',
          data: { organUid: rootState.user.orgValue, upVproUid: uid }
        },
        { root: true }
      )
      const rows = Array.isArray(res && res.data) ? res.data : []
      const unExitLaborCompanys = resetAttribute(
        rows,
        'vProjectName',
        'vProjectUid'
      )
      commit(
        'baseSet',
        {
          module: 'bidSection',
          key: 'unExitLaborCompanys',
          value: unExitLaborCompanys
        },
        { root: true }
      )
      return unExitLaborCompanys
    },
    // 新增项目标段
    async addBidSection({ commit, dispatch, rootState, rootGetters }, data) {
      const payload = applyWorkbenchParent(
        rootState,
        rootGetters,
        Object.assign({}, data)
      )
      const res = await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/temp/projectinfo',
          data: payload,
          msg: true
        },
        { root: true }
      )
      if (res.data && res.data.uid) {
        commit(
          'currentUidsSet',
          {
            key: 'bidSection',
            value: res.data.uid
          },
          { root: true }
        )
        commit(
          'currentUidsSet',
          {
            key: 'baseInfo',
            value: res.data.uid
          },
          { root: true }
        )
        commit(
          'currentUidsSet',
          {
            key: 'overview',
            value: res.data.uid
          },
          { root: true }
        )
        commit(
          'currentUidsSet',
          {
            key: 'contract',
            value: res.data.uid
          },
          { root: true }
        )
      }
      return res
    },
    // 删除项目标段
    async deleteBidSection({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/temp/projectinfo',
          data: { uid: uid },
          msg: '删除项目标段成功'
        },
        { root: true }
      )
      return res
    },
    // 获取项目标段列表
    async getBidSectionList({ rootState, rootGetters, commit, dispatch }) {
      await Promise.all([
        dispatch('dictionary/getOrgGroup', null, { root: true }),
        dispatch('dictionary/getBuildStatus', null, { root: true })
      ])
      // 企业/工程：优先用服务端默认标段覆盖本机缓存，保证换设备也能带上
      await dispatch('syncDefaultBidFromServer')
      var queryValues = Object.assign({}, rootState.queryValues.bidSection)
      queryValues.page = rootState.page.bidSection
      queryValues.limit = rootState.limit.bidSection
      if (
        rootState.currentStep &&
        rootState.currentStep.theme === 'projectSection'
      ) {
        const orgType = rootGetters['user/orgType']
        if (orgType === 'bigPro') {
          queryValues.projectUid = rootState.user.orgValue
          delete queryValues.parentCompanyUid
        } else {
          queryValues.parentCompanyUid = rootState.user.orgValue
        }
      } else {
        delete queryValues.parentCompanyUid
      }
      queryValues = removeNull(queryValues)
      const res = await dispatch(
        'baseRequest',
        { method: 'GET', url: '/organize/temp/projectinfolist', data: queryValues },
        { root: true }
      )
      if (res.data) {
        commit(
          'baseSet',
          { module: 'bidSection', key: 'bidSectionDetail', value: null },
          { root: true }
        )
        const bidSectionList = res.data
        if (bidSectionList.result && bidSectionList.result.length) {
          const orgGroupDict = rootState.dictionarys.orgGroup
          const buildStatusDict = rootState.dictionarys.buildStatus
          const loginOrgUid = rootState.user && rootState.user.orgValue
          bidSectionList.result.forEach((item) => {
            item.qcodeInfo = buildBidQcodeInfo(item, item.orgUid)
            item.discloseQcodeInfo =
              (item.qcodeInfo && item.qcodeInfo.discloseQcodeInfo) ||
              buildDiscloseQcodeInfo(item, item.orgUid)
            item.orgGroup = matchDictLabel(orgGroupDict, item.orgGroup)
            item.buildStatus = matchDictLabel(
              buildStatusDict,
              pickBuildStatus(item)
            )
            item.isDefaultBid = isDefaultBidUid(loginOrgUid, item.orgUid)
          })
        }
        commit(
          'baseSet',
          { module: 'bidSection', key: 'bidSectionList', value: bidSectionList },
          { root: true }
        )
      }
    },
    async loadBidFormDetail({ dispatch }, uid) {
      if (!uid) return {}
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/temp/projectinfodetail',
          data: { orgUid: uid }
        },
        { root: true }
      )
      return res && res.data ? res.data : {}
    },
    async fetchBidSectionDetail({ state, commit, dispatch }, uid) {
      if (state.bidSectionDetail && state.bidSectionDetail._uid === uid) {
        return state.bidSectionDetail
      }
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/temp/projectinfodetail',
          data: { orgUid: uid }
        },
        { root: true }
      )
      const detail = Object.assign({ _uid: uid }, res.data ? res.data : {})
      commit(
        'baseSet',
        { module: 'bidSection', key: 'bidSectionDetail', value: detail },
        { root: true }
      )
      return detail
    },
    // 查询单个项目标段全部信息
    async getBidSectionInfo({ rootState, commit, dispatch }, uid) {
      const pageName = rootState.currentStep.step.slice(0, -4)
      const bidSectionUid = uid ? uid : rootState.currentUids[pageName]
      commit(
        'baseSet',
        { module: 'bidSection', key: 'bidSectionDetail', value: null },
        { root: true }
      )
      commit(
        'baseSet',
        {
          module: 'bidSection',
          key: 'bidSectionBaseInfo',
          value: { uid: '', _fetchedAt: 0 }
        },
        { root: true }
      )
      const bidSectionBaseInfo = await dispatch(
        'getBidSectionBaseInfo',
        bidSectionUid
      )
      const bidSectionOverview = await dispatch(
        'getBidSectionOverview',
        bidSectionUid
      )
      const bidSectionContract = await dispatch(
        'getBidSectionContract',
        bidSectionUid
      )
      const bidSectionInfo = {
        baseInfo: bidSectionBaseInfo,
        overview: bidSectionOverview,
        contract: bidSectionContract,
        qcodeInfo: bidSectionBaseInfo.qcodeInfo
      }
      return bidSectionInfo
    },
    // 获取单个项目标段基础信息
    async getBidSectionBaseInfo({ commit, dispatch, rootState }, uid) {
      const detail = await dispatch('fetchBidSectionDetail', uid)
      var bidSectionBaseInfo = Object.assign({}, detail)
      bidSectionBaseInfo.uid = uid
      bidSectionBaseInfo.name = bidSectionBaseInfo.orgName
      bidSectionBaseInfo.activityType = bidSectionBaseInfo.orgGroup
      bidSectionBaseInfo.isCompanyKey =
        bidSectionBaseInfo.companyKey || bidSectionBaseInfo.isCompanyKey
      bidSectionBaseInfo.buildCompany = bidSectionBaseInfo.buildCompanyUid
      bidSectionBaseInfo.parentCompanyUid = pickText(detail, [
        'parentCompanyUid',
        'parentcompanyuid'
      ])
      bidSectionBaseInfo.parentCompanyName = pickText(detail, [
        'parentCompanyName',
        'parentcompanyname'
      ])
      bidSectionBaseInfo.projectUid = pickText(detail, [
        'projectUid',
        'projectuid'
      ])
      bidSectionBaseInfo.projectName = pickText(detail, [
        'projectName',
        'projectname'
      ])
      bidSectionBaseInfo.qcodeInfo = buildBidQcodeInfo(bidSectionBaseInfo, uid)
      bidSectionBaseInfo.discloseQcodeInfo =
        (bidSectionBaseInfo.qcodeInfo &&
          bidSectionBaseInfo.qcodeInfo.discloseQcodeInfo) ||
        buildDiscloseQcodeInfo(bidSectionBaseInfo, uid)
      bidSectionBaseInfo._fetchedAt = Date.now()
      const companyUid = bidSectionBaseInfo.parentCompanyUid
      if (companyUid) {
        const enterprises = Array.isArray(rootState.enterprise.enterprises)
          ? rootState.enterprise.enterprises.slice()
          : []
        if (
          !enterprises.some((item) => item && String(item.value) === companyUid)
        ) {
          enterprises.unshift({
            label: bidSectionBaseInfo.parentCompanyName || companyUid,
            value: companyUid,
            companyUid: companyUid,
            companyName: bidSectionBaseInfo.parentCompanyName || companyUid
          })
          commit(
            'baseSet',
            { module: 'enterprise', key: 'enterprises', value: enterprises },
            { root: true }
          )
        }
      }
      const projectUid = bidSectionBaseInfo.projectUid
      if (projectUid) {
        const projectNames = Array.isArray(rootState.project.projectNames)
          ? rootState.project.projectNames.slice()
          : []
        if (
          !projectNames.some((item) => item && String(item.value) === projectUid)
        ) {
          projectNames.unshift({
            label: bidSectionBaseInfo.projectName || projectUid,
            value: projectUid,
            projectUid: projectUid,
            projectName: bidSectionBaseInfo.projectName || projectUid
          })
          commit(
            'baseSet',
            { module: 'project', key: 'projectNames', value: projectNames },
            { root: true }
          )
        }
      }
      commit(
        'baseSet',
        {
          module: 'bidSection',
          key: 'bidSectionBaseInfo',
          value: bidSectionBaseInfo
        },
        { root: true }
      )
      return bidSectionBaseInfo
    },
    // 获取单个项目标段工程概况
    async getBidSectionOverview({ commit, dispatch }, uid) {
      const detail = await dispatch('fetchBidSectionDetail', uid)
      var bidSectionOverview = Object.assign({}, detail)
      const lng = bidSectionOverview.xlon
      const lat = bidSectionOverview.ylat
      bidSectionOverview.uid = uid
      bidSectionOverview.position = bidSectionOverview.buildAddress
      bidSectionOverview.gpsInfo =
        lng !== undefined && lng !== null && lng !== '' &&
        lat !== undefined && lat !== null && lat !== ''
          ? lng + ',' + lat
          : ''
      bidSectionOverview.gps = {
        lng: lng !== undefined && lng !== null && lng !== '' ? lng : '',
        lat: lat !== undefined && lat !== null && lat !== '' ? lat : ''
      }
      bidSectionOverview.projectSize = bidSectionOverview.quantitiesMileage
      commit(
        'baseSet',
        {
          module: 'bidSection',
          key: 'bidSectionOverview',
          value: bidSectionOverview
        },
        { root: true }
      )
      return bidSectionOverview
    },
    // 获取单个项目标段承包信息
    async getBidSectionContract({ commit, dispatch }, uid) {
      const detail = await dispatch('fetchBidSectionDetail', uid)
      const bidSectionContract = Object.assign({}, detail)
      bidSectionContract.uid = uid
      bidSectionContract.dateAward = bidSectionContract.dateaward
      commit(
        'baseSet',
        {
          module: 'bidSection',
          key: 'bidSectionContract',
          value: bidSectionContract
        },
        { root: true }
      )
      return bidSectionContract
    },
    // 编辑项目标段档案信息
    async editBidSection({ commit, dispatch, rootState, rootGetters }, data) {
      const payload = applyWorkbenchParent(
        rootState,
        rootGetters,
        Object.assign({}, data)
      )
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/temp/projectinfo',
          data: payload,
          msg: '编辑项目标段成功'
        },
        { root: true }
      )
      commit(
        'baseSet',
        { module: 'bidSection', key: 'bidSectionDetail', value: null },
        { root: true }
      )
      return res
    }
  }
}
